import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useWatchlist } from '../hooks/useWatchlist';

/**
 * Toggle button that pins / unpins a (kind, key) pair on the watchlist.
 * Filled star + accent color when watched, outline otherwise.
 *
 * `compact` mode is a small icon-only button (used in list rows). The
 * default rendering is icon + label (used in detail-view headers).
 */
const WatchlistPinButton = ({ kind, itemKey, label, compact = false }) => {
  // `itemKey` (not `key`) -- React reserves the `key` prop and strips it
  // from `props` before the component receives it.
  const { add, remove, isWatched } = useWatchlist();
  const [busy, setBusy] = useState(false);
  const watched = isWatched(kind, itemKey);

  const onClick = async (e) => {
    // List rows wrap this button -- block parent navigation on click.
    e?.preventDefault();
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (watched) await remove(kind, itemKey);
      else await add({ kind, key: itemKey, label });
    } catch (err) {
      console.error('Watchlist toggle failed', err);
    } finally {
      setBusy(false);
    }
  };

  const title = watched ? 'Remove from watchlist' : 'Add to watchlist';
  const fill = watched ? 'var(--warning-color)' : 'none';
  const stroke = watched ? 'var(--warning-color)' : 'var(--text-secondary)';

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        disabled={busy}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: busy ? 'wait' : 'pointer',
          padding: '0.25rem',
          display: 'inline-flex',
          alignItems: 'center',
          color: stroke,
          opacity: busy ? 0.6 : 1,
        }}
      >
        <Star size={16} fill={fill} stroke={stroke} strokeWidth={1.75} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={busy}
      className={`btn ${watched ? 'btn-secondary' : 'btn-secondary'}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
    >
      <Star size={16} fill={fill} stroke={stroke} strokeWidth={1.75} />
      {watched ? 'Watching' : 'Watch'}
    </button>
  );
};

export default WatchlistPinButton;
